// Row helpers and lead snapshots for the Supabase DialerApi (supabaseDialerApi.ts).
import type { SupabaseClient } from "@supabase/supabase-js";
import type { CallOutcome, LeadSnapshot } from "../../domain/dialer/types";
import { DialerApiError, DialerNetworkError } from "./types";
import { contactLabel } from "../../domain/dialer/contactCall"; // GAVION demo-contact

export type Failure = {
  message: string;
  details?: string | null;
  code?: string | null;
} | null;
export type Row = Record<string, unknown>;
export const DEFAULT_TZ = "America/Toronto";

const GATEWAY = new Set([502, 503, 504]);
const TRANSPORT = /^(TypeError|FetchError|AbortError):/;

/**
 * supabase-js never throws on a transport failure: it returns `status: 0` and an error without a
 * Postgres code ("TypeError: Failed to fetch"). Those, and gateway errors, are network errors.
 */
const isTransportFailure = (error: NonNullable<Failure>, status?: number) =>
  status === 0 ||
  (status !== undefined && GATEWAY.has(status)) ||
  (!error.code && TRANSPORT.test(error.message ?? ""));

/**
 * Throws the RPC's error code (raise exception '<code>') with its detail (e.g. the suggested callback
 * time), or a DialerNetworkError when the server never answered.
 */
export const must = <T>({
  data,
  error,
  status,
}: {
  data: T;
  error: Failure;
  status?: number;
}): T => {
  if (!error) return data;
  if (isTransportFailure(error, status))
    throw new DialerNetworkError(error.message || "network_error");
  throw new DialerApiError(error.message, error.details ?? undefined);
};

export const str = (v: unknown): string | null =>
  v === null || v === undefined ? null : String(v);

/** companies row + first contact + assigned rep → LeadSnapshot (same shape as the fake API's snapshot). */
const toSnapshot = (
  c: Row,
  contact: Row | null,
  owner: Row | null,
): LeadSnapshot => ({
  id: Number(c.id),
  phone_e164: str(c.phone_e164),
  phone_valid: (c.phone_valid as boolean | undefined) ?? true,
  prospect_status:
    (c.prospect_status as LeadSnapshot["prospect_status"]) ?? "new",
  attempt_count: Number(c.attempt_count ?? 0),
  next_callback_at: str(c.next_callback_at),
  cooldown_until: str(c.cooldown_until),
  assigned_sales_id: (c.assigned_sales_id as number | null) ?? null,
  locked_by: (c.locked_by as number | null) ?? null,
  locked_until: str(c.locked_until),
  last_called_at: str(c.last_called_at),
  high_quality: (c.high_quality as boolean | undefined) ?? false,
  fit_score: (c.fit_score as number | null) ?? null,
  date_added: str(c.date_added),
  timezone: str(c.timezone) ?? DEFAULT_TZ,
  trade: str(c.trade),
  region: str(c.region),
  ...(c.is_test === true ? { is_test: true } : {}), // GAVION test mode
  name: String(c.name ?? ""),
  city: str(c.city),
  website: str(c.website),
  email: str(c.email),
  language: c.language === "en" ? "en" : "fr",
  last_call_outcome: (c.last_call_outcome as CallOutcome | null) ?? null,
  contact_name: contact ? contactLabel(contact) || null : null, // GAVION demo-contact: else his email
  contact_role: contact ? str(contact.role_title) : null,
  assigned_name: owner ? `${owner.first_name} ${owner.last_name}` : null,
});

/** A single row where a list was expected (and null) still reads as a list. */
const asRows = (data: unknown): Row[] =>
  Array.isArray(data) ? (data as Row[]) : data ? [data as Row] : [];

/** Lead snapshots in `ids` order (unknown ids are dropped): three reads, whatever the length. */
export async function loadSnapshots(
  db: SupabaseClient,
  ids: number[],
): Promise<LeadSnapshot[]> {
  if (ids.length === 0) return [];
  const companies = asRows(
    must(await db.from("companies").select("*").in("id", ids)),
  );
  const contacts = asRows(
    must(
      await db
        .from("contacts")
        .select("company_id, first_name, last_name, role_title, email_jsonb") // GAVION demo-contact: label fallback
        .in("company_id", ids)
        .order("id"),
    ),
  );
  const ownerIds = [
    ...new Set(companies.map((c) => c.assigned_sales_id).filter((v) => v)),
  ];
  const owners = ownerIds.length
    ? asRows(
        must(
          await db
            .from("sales")
            .select("id, first_name, last_name")
            .in("id", ownerIds),
        ),
      )
    : [];
  return ids.flatMap((id) => {
    const c = companies.find((x) => Number(x.id) === id);
    if (!c) return [];
    const contact = contacts.find((x) => Number(x.company_id) === id) ?? null;
    const owner = owners.find((x) => x.id === c.assigned_sales_id) ?? null;
    return [toSnapshot(c, contact, owner)];
  });
}

export async function loadSnapshot(
  db: SupabaseClient,
  id: number,
): Promise<LeadSnapshot> {
  const [lead] = await loadSnapshots(db, [id]);
  if (!lead) throw new DialerApiError("not_found");
  return lead;
}
